/* eslint-disable react/require-default-props */
import React from 'react'
import PropTypes from 'prop-types'
import { Image, Typography } from '@telus/koodo-ui-kit'
import Heading1 from '@apps/components/Typography/Heading1'
import Heading2 from '@apps/components/Typography/Heading2'

// Error layout shared by ProcessFailure and ErrorSection (splash / 404 /
// ErrorBoundary). Desktop ("H1"): two columns — illustration left, text right.
// Mobile ("HH1"): single left-aligned column with the illustration inline after
// the body. Content/logic stay with the consuming pages; only presentation here.
// Two vertical treatments share this layout:
//  - 'error' (default) — ProcessFailure / splash / 404. Text column is centred
//    against the illustration with a tight rhythm.
//  - 'notice' — already-activated. Heading top-aligns with the illustration so
//    it sits high on the page (nudged down 2px to sit level with the artwork),
//    with a roomier rhythm and a wider gap above the CTAs.
const LAYOUT_VARIANTS = {
  error: {
    row: 'items-center', content: 'space-y-4', cta: 'pt-4', mobileImage: ''
  },
  notice: {
    row: 'items-center md:items-start',
    content: 'space-y-10 md:pt-[2px]',
    // The wide gap above the CTAs is a desktop-only rule — on mobile (HH1) the
    // illustration sits directly above them with just the column rhythm between.
    // sm:items-start keeps each CTA at its natural height instead of stretching
    // both to the taller one.
    cta: 'pt-0 md:pt-16 items-stretch sm:items-start',
    // Mobile (HH1) wants a touch more room between the body copy and the artwork.
    mobileImage: 'pt-4'
  }
}

const ActivationErrorLayout = ({
  imageUrl,
  imageAlt = '',
  heading,
  body,
  errorCode = null,
  variant = 'error',
  children
}) => {
  const {
    row, content, cta, mobileImage
  } = LAYOUT_VARIANTS[variant] || LAYOUT_VARIANTS.error

  // UDS Image renders with its own intrinsic sizing. Keep the Figma visual by
  // centering the square artwork inside the design boxes instead of stretching it.
  const renderIllustration = (width) => (
    <Image alt={imageAlt} src={imageUrl} width={width} loading="eager" />
  )

  return (
    <div className={`flex flex-col md:flex-row justify-center max-w-6xl m-auto px-4 pt-12 md:pt-[140px] pb-12 gap-8 md:gap-16 ${row}`}>
      {/* Desktop ("H1"): illustration in the left column. Hidden on mobile —
          there it renders inline after the body instead (see below). */}
      <div className="hidden md:flex w-full max-w-[389px] shrink-0 justify-center">
        {renderIllustration(370)}
      </div>

      {/* Text column — left-aligned. Order matches Figma (desktop H1 + mobile
          HH1): heading → issue/error code → body → (mobile illustration) →
          action(s). UDS sizes: heading 54px bold (36px mobile), body 16px
          regular, all #000000. */}
      <div className={`flex flex-col w-full md:max-w-[668px] text-left ${content}`}>
        <Heading1 tokens={{ fontSize: 54, fontWeight: 700, color: '#000000' }} className="hidden md:block">{heading}</Heading1>
        <Heading2 tokens={{ fontSize: 36, fontWeight: 700, color: '#000000' }} className="block md:hidden">{heading}</Heading2>

        {errorCode}

        <Typography tokens={{ fontSize: 16, fontWeight: 400, color: '#000000' }} block>{body}</Typography>

        {/* Mobile ("HH1"): illustration between body and action(s), centered. */}
        <div className={`flex md:hidden w-full max-w-[306px] mx-auto justify-center ${mobileImage}`}>
          {renderIllustration(306)}
        </div>

        {children && (
          <div className={`flex flex-col sm:flex-row gap-4 justify-start ${cta}`}>
            {children}
          </div>
        )}
      </div>
    </div>
  )
}

ActivationErrorLayout.propTypes = {
  imageUrl: PropTypes.string,
  imageAlt: PropTypes.string,
  heading: PropTypes.node,
  body: PropTypes.node,
  errorCode: PropTypes.node,
  variant: PropTypes.oneOf(['error', 'notice']),
  children: PropTypes.node
}

export default ActivationErrorLayout
